import { Component, OnDestroy, OnInit, DoCheck } from '@angular/core';
import { Router } from '@angular/router';
import { AuthorizeService } from '../../api-authorization/authorize.service';
import { SharedService } from '../services/shared.service';
import { FaqService } from '../services/faq.service';
import { IFaqDto } from '../interfaces/Faq/ifaq-dto';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-nav-menu',
  templateUrl: './nav-menu.component.html',
  styleUrls: ['./nav-menu.component.css']
})
export class NavMenuComponent implements OnInit, OnDestroy, DoCheck {

  constructor(private authorizeService: AuthorizeService,
              private sharedService: SharedService,
              private faqService: FaqService,
              private router: Router) { }
  isAuthenticated: boolean = false;
  /** Active FAQs for FAQ/RESOURCES menu, sorted by displayOrder. */
  menuFaqs: IFaqDto[] = [];
  private menuChangedSub: Subscription | undefined;
  private menuLoadSub: Subscription | undefined;
  private menuLoadTimer: any = null;
  private initialLoadStarted = false;

  ngOnInit(): void {
    this.authorizeService.isAuthenticated().subscribe(data => {
      this.isAuthenticated = data;
      if (!data) {
        this.menuFaqs = [];
        this.initialLoadStarted = false;
        this.cancelDeferredMenuLoad();
      }
    });

    this.menuChangedSub = this.faqService.menuChanged$.subscribe(() => {
      // After FAQ Management save: refresh in the background (do not block other menus).
      this.scheduleMenuFaqsLoad();
    });
  }

  ngDoCheck(): void {
    if (!this.isAuthenticated || this.sharedService.userPrivilegeVo == null) {
      return;
    }
    if (this.initialLoadStarted) {
      return;
    }
    this.initialLoadStarted = true;
    this.startInitialMenuLoad();
  }

  ngOnDestroy(): void {
    if (this.menuChangedSub) {
      this.menuChangedSub.unsubscribe();
    }
    this.cancelDeferredMenuLoad();
  }

  private startInitialMenuLoad() {
    // Always load from API (server IMemoryCache). Defer so other nav menus are not blocked.
    this.scheduleMenuFaqsLoad();
  }

  /** Defer FAQ menu API so nav privilege/other dropdown work is not blocked. */
  private scheduleMenuFaqsLoad() {
    const self = this;
    this.cancelDeferredMenuLoad();
    this.menuLoadTimer = setTimeout(function () {
      self.menuLoadTimer = null;
      self.loadMenuFaqs();
    }, 0);
  }

  private cancelDeferredMenuLoad() {
    if (this.menuLoadTimer != null) {
      clearTimeout(this.menuLoadTimer);
      this.menuLoadTimer = null;
    }
    if (this.menuLoadSub) {
      this.menuLoadSub.unsubscribe();
      this.menuLoadSub = undefined;
    }
  }

  loadMenuFaqs() {
    if (this.menuLoadSub) {
      this.menuLoadSub.unsubscribe();
      this.menuLoadSub = undefined;
    }

    this.menuLoadSub = this.faqService.getActiveFaqsForMenu().subscribe(faqs => {
      this.menuFaqs = this.buildMenuFaqs(faqs);
      this.menuLoadSub = undefined;
    }, err => {
      console.error(err);
      this.menuLoadSub = undefined;
      if (!this.menuFaqs || this.menuFaqs.length === 0) {
        this.menuFaqs = [];
      }
    });
  }

  private buildMenuFaqs(faqs: IFaqDto[]): IFaqDto[] {
    if (!faqs || faqs.length === 0) {
      return [];
    }

    // Static pages stay outside FAQ Management menu items.
    const reservedCodes = ['info', 'solvoit'];

    return faqs
      .filter(f => {
        if (!f || !f.isActive || !f.code) {
          return false;
        }
        return reservedCodes.indexOf(String(f.code).toLowerCase()) === -1;
      })
      .sort((a, b) => {
        const orderA = a.displayOrder != null ? a.displayOrder : 0;
        const orderB = b.displayOrder != null ? b.displayOrder : 0;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        const titleA = a.title ? a.title : '';
        const titleB = b.title ? b.title : '';
        return titleA.localeCompare(titleB);
      });
  }

  isExpanded = false;

  collapse() {
    this.isExpanded = false;
  }

  toggle() {
    this.isExpanded = !this.isExpanded;
  }

  handleClick() {
    const url = this.sharedService.ApiUrl + "Identity/Account/Manage/ChangePassword";
    window.location.href = url;
  }

  downloadPos() {
    window.open("https://fileservices.solvoit.ca/SolvoPos.zip", "_blank");
  }

  downloadDatabase() {
    window.open("https://fileservices.solvoit.ca/img.zip", "_blank");
  }

  goToPharmacyReview(event: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.router.navigate(['/invoice/review']);
  }

  public isActiveItemMenu(page: string): boolean {
    const currentUrl = this.router.url;
    // Remove leading slash if present in page for consistency
    const searchPage = page.startsWith('/') ? page.substring(1) : page;
    
    // Check if the URL starts with /searchPage
    // This prevents /settings/faq from matching 'faq'
    return currentUrl.startsWith('/' + searchPage);
  }
}
